"use client"
import Link from "next/link"
import { Button } from "@/components/ui/button"
import { ArrowRight, Camera, Wand2 } from "lucide-react"
import { GalleryCarousel } from "./GalleryCarousel"
import { BeforeAfterSlider } from "../../components/BeforeAfterSlider"

export function HomePage() {
  return (
    <div className="flex flex-col w-full min-h-screen overflow-x-hidden">
      {/* Hero Section */}
      <section className="relative w-full py-16 md:py-24 lg:py-32 overflow-hidden touch-pan-y">
        <div className="container px-4 mx-auto md:px-6">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-16 items-center">
            <div className="space-y-6">
              <h1 className="text-4xl font-bold tracking-tight md:text-5xl lg:text-6xl">
                Transform Your Space with <span className="text-[#40A1AB]">AI-Powered</span> Interior Design
              </h1>
              <p className="text-lg text-gray-600 md:text-xl">
                Generate stunning interior designs in seconds. Upload your room or start from scratch to create your
                dream space.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link href="/designer">
                  <Button className="w-full sm:w-auto bg-[#40A1AB] hover:bg-[#3A919A] text-white">
                    Start Designing <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <Link href="/gallery">
                  <Button variant="outline" className="w-full sm:w-auto bg-transparent">
                    View Gallery
                  </Button>
                </Link>
              </div>
            </div>
            <div className="relative h-[300px] md:h-[400px] lg:h-[500px] rounded-lg overflow-hidden shadow-xl">
              <BeforeAfterSlider
                beforeImage="/images/magic-original.png"
                afterImage="/images/magic-transformed.png"
                beforeAlt="Traditional room before AI transformation"
                afterAlt="Modern farmhouse room after AI transformation"
                className="w-full h-full rounded-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="w-full py-16 bg-white">
        <div className="container px-4 mx-auto md:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold md:text-4xl">How It Works</h2>
            <p className="mt-4 text-lg text-gray-600">Create your dream space in just a few simple steps</p>
          </div>

          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
            <div className="flex flex-col items-center text-center p-6 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-center w-12 h-12 mb-4 bg-[#40A1AB] rounded-full text-white">
                <Camera className="h-6 w-6" />
              </div>
              <h3 className="text-xl font-semibold">Upload Your Room</h3>
              <p className="mt-2 text-gray-600">
                Upload a photo of your room or start from scratch with our room templates.
              </p>
            </div>

            <div className="flex flex-col items-center text-center p-6 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-center w-12 h-12 mb-4 bg-[#40A1AB] rounded-full text-white">
                <Wand2 className="h-6 w-6" />
              </div>
              <h3 className="text-xl font-semibold">Customize Your Design</h3>
              <p className="mt-2 text-gray-600">Choose your preferred style, color preferences, and room details.</p>
            </div>

            <div className="flex flex-col items-center text-center p-6 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-center w-12 h-12 mb-4 bg-[#40A1AB] rounded-full text-white">
                <ArrowRight className="h-6 w-6" />
              </div>
              <h3 className="text-xl font-semibold">Generate & Refine</h3>
              <p className="mt-2 text-gray-600">
                Our Room Designer generates multiple design options that you can refine and save.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Carousel Section */}
      <section className="w-full py-16">
        <div className="container px-4 mx-auto md:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold md:text-4xl">Latest Designs</h2>
            <p className="mt-4 text-lg text-gray-600">
              Browse through our community's latest interior design transformations
            </p>
          </div>

          <GalleryCarousel />

          <div className="mt-12 text-center">
            <Link href="/gallery">
              <Button variant="outline">View All Designs</Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="w-full py-16 bg-[#40A1AB] text-white">
        <div className="container px-4 mx-auto md:px-6">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold md:text-4xl">Ready to Transform Your Space?</h2>
            <p className="mt-4 text-lg opacity-90">
              Start designing your dream interior today with our AI-powered platform.
            </p>
            <div className="mt-8">
              <Link href="/designer">
                <Button className="bg-white text-[#40A1AB] hover:bg-gray-100">Start Designing Now</Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
